/**
 * Order two tree labels the way a person reads them.
 *
 * Labels are strings because crews renumber with suffixes — "12A" is inserted
 * after "12" on a map already printed and sitting in a truck. Plain string
 * comparison would then put "100" before "41" and "12A" before "2", so the
 * number is compared as a number and whatever follows breaks ties.
 *
 * A leading "#" is skipped rather than required: the real data writes labels
 * both ways, and a sort that only worked on one of them would look right on
 * every site until the first one that did not use it.
 *
 * Any other leading letters stay. "A12" is an A label, not another "12", so
 * the two do not sort as the same tree.
 */
export function compareTreeLabels(a: string, b: string): number {
  const parse = (label: string): [string, number, string] => {
    const trimmed = label.trim().replace(/^#/, '')
    const match = /^(\D*?)(\d+)(.*)$/.exec(trimmed)
    if (match === null) return [trimmed, Number.MAX_SAFE_INTEGER, '']
    return [match[1] ?? '', Number(match[2]), match[3] ?? '']
  }
  const [aPrefix, aNumber, aRest] = parse(a)
  const [bPrefix, bNumber, bRest] = parse(b)
  if (aPrefix !== bPrefix) return aPrefix.localeCompare(bPrefix)
  if (aNumber !== bNumber) return aNumber - bNumber
  return aRest.localeCompare(bRest)
}
